import {
  Dropdown,
  IconButton,
  SvgArchive,
  SvgCopy,
  SvgDomain,
  SvgDownload,
  SvgEdit,
  SvgFolder,
  SvgLock,
  SvgMoreHoriz,
  SvgNotes,
  SvgStar,
  SvgStarOutline,
  SvgTrash,
  SvgUnarchive,
  SvgUsersAdd,
} from '@loomhq/lens';
import copy from 'copy-to-clipboard';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import { updateShowMoveModal } from 'actions/moveModal';
import { showCollaborateModal } from 'actions/videosDashboard';
import { DESKTOP_MIN_WIDTH } from 'constants/breakpoints';
import {
  CLICKED_SHARE_WITH_TEAM,
  CLICKED_UNDO_SHARE_WITH_TEAM,
  DUPLICATE_ITEM_CLICK,
  ORGANIZE_VIDEO,
} from 'constants/events';
import {
  ROLLOUT_SHARED_WITH_ME,
  STORE_FILLER_WORD_RANGES,
} from 'constants/featureFlag';
import * as menuOptions from 'constants/menuOptions';
import {
  DOWNLOAD_VIDEO_INFO_MODAL,
  PRIVACY_CHANGE_MOVE_CONFIRMATION_MODAL,
} from 'constants/modalTypes';
import { LOOM_URI } from 'constants/routes';
import { SHOW_DOWNLOAD_VIDEO_INFO_MODAL } from 'constants/triggers';
import { VIDEO } from 'constants/videosDashboard';
import { startDownload } from 'creators/header';
import {
  archiveVideoCreator,
  deleteVideoCreator,
  handleToggleTranscriptCreator,
  moveVideoCreator,
  toggleVideoFavoriteCreator,
} from 'creators/shareVideo';
import { completeTriggerCreator } from 'creators/triggers';
import { duplicateVideoCreator } from 'creators/video';
import * as analytics from 'utilities/analytics';

import useWindowSize from 'hooks/useWindowSize';
import { useFeatureFlagValue, useFlagIsActivated } from 'hooks/featureFlag';
import { useShouldShowTrigger } from 'hooks/triggers';
import { selectOverflowMenuScopes } from 'selectors/overflowMenu';
import { selectUserMustEnterPassword } from 'selectors/shareVideo';

import {
  MY_VIDEOS_FOLDER_NAME,
  SPECIAL_FOLDER_ID_MY_VIDEOS,
  SPECIAL_FOLDER_ID_TEAM_VIDEOS,
  TEAM_VIDEOS_FOLDER_NAME,
} from '../../../../../shared/constants/folders';
import { VIDEO_PRIVACY_OWNER } from '../../../../../shared/constants/video';
import {
  PRIVATE_WORKSPACE,
  TEAM_WORKSPACE,
} from '../../../../../shared/constants/workspaces';

import HideDuringDowntime from 'components/hide-during-downtime';

const getOptions = ({
  canArchive,
  canDelete,
  canInviteToEdit,
  canStar,
  dispatch,
  downloadUrl,
  hasToolsAccess,
  triggerFocusTitle,
  video,
  videoIsTeamShared,
  showTranscriptOption,
  shouldDisableTranscript,
  canDownload,
  shouldShowFillerWordRemoval = false,
}) => {
  const selectedItem = { id: video.id, name: video.name, type: 'video' };
  const renameVideo = () => triggerFocusTitle();
  const moveVideo = () => {
    dispatch(updateShowMoveModal(true, [selectedItem]));

    analytics.track(ORGANIZE_VIDEO, {
      root_folder: video.folder_id,
      is_owner: video.current_user_is_owner,
    });
  };
  const inviteToEdit = () => {
    dispatch(
      showCollaborateModal(video.id, video.name, false, VIDEO, video.owner_id)
    );
  };

  const archiveVideo = async () => {
    await dispatch(archiveVideoCreator(video.id));
  };

  const onTranscriptClick = async () => {
    await dispatch(handleToggleTranscriptCreator());
  };

  const deleteVideo = () => {
    if (!confirm('Are you sure you want to permanently delete this video?')) {
      return;
    }

    dispatch(deleteVideoCreator(video.id));
  };

  const duplicateVideo = () => {
    const newName = `Copy of ${video.name}`;

    dispatch(duplicateVideoCreator(video.id, newName))
      .then(newVideo => {
        const url = `${LOOM_URI}/share/${newVideo.id}`;

        copy(url, { format: 'text/plain' });
        setTimeout(() => {
          window.open(url, '_blank');
        }, 1000);
      })
      .then(() => {
        analytics.track(DUPLICATE_ITEM_CLICK, {
          entity_id: video.id,
          entity_type: 'video',
          click_source: 'share-video',
        });
      });
  };

  const toggleFavoriteStatus = () => {
    dispatch(toggleVideoFavoriteCreator(video));

    // we don't store the video record in a redux store,
    // so we just update the record in memory
    // hacky but works because the menu is closed when any option is chosen
    // so when we next render the menu, this will appear correctly
    video.isUserFavorite = !video.isUserFavorite;
  };

  const moveVideoToTeam = () => {
    analytics.track(CLICKED_SHARE_WITH_TEAM);
    dispatch(
      moveVideoCreator(
        SPECIAL_FOLDER_ID_TEAM_VIDEOS,
        TEAM_VIDEOS_FOLDER_NAME,
        false,
        TEAM_WORKSPACE
      )
    );
  };

  const moveVideoToPersonal = () => {
    analytics.track(CLICKED_UNDO_SHARE_WITH_TEAM);
    dispatch(
      moveVideoCreator(
        SPECIAL_FOLDER_ID_MY_VIDEOS,
        MY_VIDEOS_FOLDER_NAME,
        false,
        PRIVATE_WORKSPACE
      )
    );
  };

  const options = [];

  if (canArchive) {
    options.push({
      title: menuOptions.ARCHIVE,
      onClick: archiveVideo,
      icon: video.archived ? SvgUnarchive : SvgArchive,
    });
  }

  if (canStar) {
    options.push({
      title: video.isUserFavorite ? menuOptions.UNSTAR : menuOptions.STAR,
      onClick: toggleFavoriteStatus,
      icon: video.isUserFavorite ? SvgStar : SvgStarOutline,
    });
  }

  if (hasToolsAccess) {
    options.push({
      title: menuOptions.DUPLICATE,
      onClick: duplicateVideo,
      icon: SvgCopy,
    });
  }

  if (canInviteToEdit) {
    options.push({
      title: menuOptions.INVITE_TO_EDIT,
      onClick: inviteToEdit,
      icon: SvgUsersAdd,
    });
  }

  if (showTranscriptOption) {
    options.push({
      title: shouldShowFillerWordRemoval
        ? menuOptions.REVIEW_TRANSCRIPT
        : menuOptions.TRANSCRIPT,
      onClick: onTranscriptClick,
      icon: SvgNotes,
      disabled: shouldDisableTranscript,
    });
  }

  if (hasToolsAccess) {
    options.push(
      {
        title: menuOptions.MOVE,
        onClick: moveVideo,
        icon: SvgFolder,
      },
      {
        title: menuOptions.RENAME,
        onClick: renameVideo,
        icon: SvgEdit,
      }
    );

    if (videoIsTeamShared) {
      options.push({
        title: menuOptions.MOVE_PERSONAL_LIBRARY,
        onClick: moveVideoToPersonal,
        icon: SvgLock,
      });
    } else {
      options.push({
        title: menuOptions.SHARE_WITH_TEAM,
        onClick: () => {
          if (video.privacy === VIDEO_PRIVACY_OWNER) {
            dispatch(
              openModal(PRIVACY_CHANGE_MOVE_CONFIRMATION_MODAL, {
                onConfirm: moveVideoToTeam,
              })
            );
          } else {
            moveVideoToTeam();
          }
        },
        icon: SvgDomain,
      });
    }
  }

  if (canDelete) {
    options.push({
      title: menuOptions.DELETE,
      onClick: deleteVideo,
      icon: SvgTrash,
    });
  }

  if (canDownload) {
    options.push({
      title: menuOptions.DOWNLOAD,
      disabled: !downloadUrl,
      icon: SvgDownload,
    });
  }

  const optionSort = (a, b) => {
    return a.title < b.title ? -1 : a.title > b.title ? 1 : 0;
  };

  return (
    options
      // we call the icon function since it's a component
      .map(({ icon, ...otherOptions }) => ({ icon: icon(), ...otherOptions }))
      .sort(optionSort)
  );
};

const OverflowMenu = ({
  downloadUrl,
  triggerFocusTitle,
  video,
  showTranscriptOption = false,
  shouldDisableTranscript = true,
  isLoggedIn,
}) => {
  const dispatch = useDispatch();
  const shouldShowDownloadInfoModal = useShouldShowTrigger(
    SHOW_DOWNLOAD_VIDEO_INFO_MODAL
  );

  const isInFillerWordRemovalFlag = useFlagIsActivated({
    flag: STORE_FILLER_WORD_RANGES,
    activationValues: [true],
  });

  const currentUserIsOwner = video.current_user_is_owner;

  const shouldShowFillerWordRemoval =
    isInFillerWordRemovalFlag && currentUserIsOwner;

  const videoIsSharedWithUser =
    Object.keys(video.collaboratePermissions).length > 0;

  const scopes = useSelector(selectOverflowMenuScopes);

  const videoIsTeamShared = useSelector(
    state => state?.share_video?.video_is_team_shared
  );

  const hasToolsAccess =
    (currentUserIsOwner || videoIsTeamShared) &&
    scopes.hasVideoToolsAccessScope;

  const sharedWithMeFlag = useFeatureFlagValue(ROLLOUT_SHARED_WITH_ME);

  const canInviteToEdit =
    (currentUserIsOwner || videoIsTeamShared) &&
    (scopes.hasVideoToolsAccessScope || scopes.hasSharedLibraryWriteScope) &&
    sharedWithMeFlag !== 'show-new';

  const passwordNeeded = useSelector(selectUserMustEnterPassword);

  // Users can only star videos in one of their shared workspaces.
  const canStar =
    isLoggedIn &&
    (currentUserIsOwner || videoIsTeamShared) &&
    scopes.hasFavoritesScope &&
    !passwordNeeded;

  const canDelete =
    (currentUserIsOwner ||
      videoIsTeamShared ||
      video.collaboratePermissions.delete) &&
    scopes.hasAuthenticatedAccess && // This should really be scope check on WRITE access.
    scopes.hasVideoToolsAccessScope;

  const canArchive =
    canDelete &&
    (videoIsTeamShared
      ? scopes.hasTeamArchiveWriteScope
      : !videoIsSharedWithUser && scopes.hasPersonalArchiveWriteScope);

  const downloadName = `${video.name || video.id}.mp4`;

  const isSmallScreen = useWindowSize().width < DESKTOP_MIN_WIDTH;

  const canDownload = isLoggedIn || isSmallScreen;

  const options = getOptions({
    canArchive,
    canDelete,
    canInviteToEdit,
    canStar,
    dispatch,
    downloadUrl,
    hasToolsAccess,
    triggerFocusTitle,
    video,
    videoIsTeamShared,
    showTranscriptOption,
    shouldDisableTranscript,
    canDownload,
    shouldShowFillerWordRemoval,
  });

  if (!options?.length) {
    return null;
  }

  const downloadVideo = () => {
    const downloadParams = {
      url: downloadUrl,
      name: downloadName,
      opts: {
        video_id: video.id,
        is_owner: currentUserIsOwner,
      },
    };

    if (shouldShowDownloadInfoModal) {
      dispatch(openModal(DOWNLOAD_VIDEO_INFO_MODAL, downloadParams));
      dispatch(completeTriggerCreator(SHOW_DOWNLOAD_VIDEO_INFO_MODAL));
    } else {
      dispatch(startDownload(downloadParams));
    }
  };

  options.find(
    opt => opt.title === menuOptions.DOWNLOAD
  ).onClick = downloadVideo;

  if (options.length === 1) {
    const { title, icon, onClick, disabled } = options[0];

    return (
      <IconButton
        altText={title}
        icon={icon}
        onClick={onClick}
        isDisabled={disabled}
      />
    );
  }

  return (
    <HideDuringDowntime>
      <Dropdown
        options={options}
        trigger={
          <IconButton altText="Toggle actions" icon={<SvgMoreHoriz />} />
        }
        menuPosition="right"
        menuZIndex={isLoggedIn ? 100 : 1001}
      />
    </HideDuringDowntime>
  );
};

export default OverflowMenu;
